import React, { createContext, useContext, useEffect, useState } from 'react'
import throttle from 'lodash.throttle'
import * as R from 'ramda'

import breakpointsInts, { atBreakpoint } from 'src/lib/util/breakpoints'
import { capitalize } from 'src/lib/util/strings'

const isBrowser = typeof window === 'object'

const ViewportContext = createContext({})

const dimensionProps = [
  'clientWidth',
  'clientHeight',
  'innerWidth',
  'innerHeight'
]

export const ViewportProvider = ({ children }) => {
  const [state, setState] = useState({
    resizing: false,
    dimensions: isBrowser && R.pick(dimensionProps, window)
  })

  useEffect(() => {
    let resizeTimeout = null

    const updateState = throttle(() => {
      setState(R.assoc('resizing', true))

      if (resizeTimeout) {
        clearTimeout(resizeTimeout)
      }

      resizeTimeout = setTimeout(() => {
        setState(R.assoc('resizing', false))
        resizeTimeout = null
      }, 520)

      // store a css var of window's vh unit to patch
      // buggy css viewport height on mobile browsers
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)

      setState(R.assoc('dimensions', R.pick(dimensionProps, window)))
    }, 500)

    const handleResize = () => {
      updateState()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <ViewportContext.Provider value={state}>
      {children}
    </ViewportContext.Provider>
  )
}

const breakpoints =
  isBrowser &&
  R.reduce(
    (acc, [key, val]) =>
      R.pipe(
        R.assoc(`max${capitalize(key)}`, atBreakpoint(val, true)),
        R.assoc(`min${capitalize(key)}`, atBreakpoint(val))
      )(acc),
    {},
    R.toPairs(breakpointsInts)
  )

export default function useViewport() {
  const { resizing, dimensions } = useContext(ViewportContext)

  return {
    resizing,
    ...breakpoints,
    ...dimensions
  }
}
